import { HttpClient } from '@angular/common/http';
import { inject, Injectable, isDevMode } from '@angular/core';
import { LoggingService } from '@maplecroft-ui/logging';
import { catchError, firstValueFrom, map, Observable, of, ReplaySubject, switchMap, take, tap } from 'rxjs';
import { MAP_PROXY } from './map-proxy.const';
import { MAPS_AUTH_ENDPOINT } from './maps-auth-endpoint';
import { CountryBoundsMap } from './models';
import { MAPS_BACKEND_URL, MAPS_SKIP_AUTHENTICATION } from './tokens';

@Injectable({
  providedIn: 'root',
})
export class MapService {
  private readonly http = inject(HttpClient);
  private readonly mapsBackendUrl = inject(MAPS_BACKEND_URL);
  private readonly skipAuthentication = inject<boolean>(MAPS_SKIP_AUTHENTICATION, { optional: true }) ?? false;
  private readonly loggingService = inject(LoggingService);
  public readonly mapsUrlPrefix = `${this.mapsBackendUrl}${isDevMode() ? MAP_PROXY : ''}`;

  private readonly _authenticated$ = new ReplaySubject<boolean>(1);
  public readonly authenticated$ = this._authenticated$.asObservable();

  constructor() {
    if (this.skipAuthentication) {
      this._authenticated$.next(true);
    } else {
      void firstValueFrom(this.authenticate()).catch(() => {
        void this.loggingService.log('Unexpected error while authenticating with the maps service', { level: 'error' });
      });
    }
  }

  public authenticate(): Observable<boolean> {
    return this.http.head<boolean>(`${this.mapsUrlPrefix}${MAPS_AUTH_ENDPOINT}`, { observe: 'response', withCredentials: true }).pipe(
      map(({ ok }) => ok),
      tap(() => this._authenticated$.next(true)),
      catchError(() => {
        void this.loggingService.log('Failed to authenticate with the maps service', { level: 'error' });
        this._authenticated$.next(false);

        return of(false);
      }),
    );
  }

  public getBounds(): Observable<CountryBoundsMap> {
    return this.authenticated$.pipe(
      take(1),
      switchMap((authenticated) => (authenticated ? of(true) : this.authenticate())),
      switchMap(() => this.http.get<CountryBoundsMap>(`${this.mapsUrlPrefix}/v3/country-bounds`)),
    );
  }
}
